import {useState, FC, useEffect, useMemo} from 'react';
import {Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Box, Button} from '@mui/material';
import SearchHeader from '@/shared/components/Table/SearchHeader';
import {ReactComponent as PdfIcon} from '@/assets/icons/pdf-icon.svg';
import {defaultPageSize, H6, MMMM_D_YYYY} from '@/utils/constants';
import {AdminPaymentStatus, IFilterGroup} from '@/utils/types';
import Pagination from '@/shared/components/Pagination';
import {billingHistory} from '@/pages/Organization/Profile/Billing/History/BillingHistory.style';
import BillingPayment from '@/pages/Organization/Profile/Billing/Payments';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {
  getAllOrganizationsTransactions,
  getPlatformFee,
  getTransactionsListSelector,
  platformFeeSelector,
} from '@/store/admin';
import {TABLES_ENTITIES} from '@/utils/enums';
import dayjs from 'dayjs';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
import Loader from '@/shared/components/Loader';
import Modal from '@/shared/components/Modal';
import {profileFormStyle} from '../General/ProfileForm/ProfileFormStyle.styles';
import Input from '@/shared/components/Inputs/Input';
import {SuperAdminService} from '@/utils/services';

const StatusColor: Record<AdminPaymentStatus, string> = {
  Paid: 'var(--success-600)',
  Pending: 'var(--warning-600)',
  Failed: 'var(--error-600)',
  Refunded: 'var(--neutral-600)',
};

const AdminBillingHistory: FC = () => {
  const dispatch = useAppDispatch();

  const transactions = useAppSelector(getTransactionsListSelector);
  const platformFee = useAppSelector(platformFeeSelector);

  const [page, setPage] = useState<number>(1);
  const [searchName, setSearchName] = useState<string>('');
  const [status, setStatus] = useState<AdminPaymentStatus | 'All'>('All');
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(true);
  const [isOpenFeeModal, setIsOpenFeeModal] = useState<boolean>(false);
  const [fee, setFee] = useState<string>(platformFee.toString());
  console.log('fee', fee);

  const handleFeeModal = () => setIsOpenFeeModal(!isOpenFeeModal);

  const filterData: IFilterGroup[] = useMemo(
    () => [
      {
        title: 'Status',
        options: (['Paid', 'Pending', 'Failed', 'Refunded', 'All'] as const).map((title) => ({
          title,
          isSelected: status === title,
        })),
      },
    ],
    [status]
  );

  const handlePressEnter = () => {
    // Trigger Add API call on Enter key press
  };
  const handleSearchChange = (value: string) => {
    setSearchName(value);
    setPage(1);
  };
  const handleFilterChange = (groupTitle: string, optionTitle: string, id?: number) => {
    if (groupTitle !== 'Status') {
      return;
    }
    setStatus((optionTitle ?? 'All') as AdminPaymentStatus | 'All');
    setPage(1);
  };

  const updatePlatformFee = async () => {
    try {
      await SuperAdminService.setDefaultPlatformFee({platformFeePercent: Number(fee)});
      await dispatch(getPlatformFee()).unwrap();
      toast.success('Platform fee updated successfully');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const getBillingHistoryData = async () => {
    setIsDataLoaded(false);

    const data = {
      page,
      pageSize: defaultPageSize,
      isOrderDesc: true,
      ...(searchName && {searchName}),
      ...(status !== 'All' && {status: status as AdminPaymentStatus}),
    };
    try {
      await dispatch(getAllOrganizationsTransactions(data)).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsDataLoaded(true);
    }
  };

  const getPlatformFeeData = async () => {
    try {
      await dispatch(getPlatformFee()).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  useEffect(() => {
    getBillingHistoryData();
    getPlatformFeeData();
  }, [page, searchName, status]);

  return (
    <Box sx={billingHistory.root}>
      {/* <BillingPayment /> */}
      <Box sx={billingHistory.percentage}>
        <Box>
          <Typography sx={billingHistory.amount}>Percentage of transaction</Typography>
          <Typography sx={billingHistory.smallText}>
            The percentage of all transactions that will be collected by the platform
          </Typography>
        </Box>
        <Box sx={billingHistory.percentButton}>
          <Typography variant="h5">{fee}%</Typography>
          <Button variant="filled" onClick={handleFeeModal}>
            Change
          </Button>
        </Box>
      </Box>
      <Box sx={{flex: '1', overflowY: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
        <Typography sx={billingHistory.tableTitle} variant={H6}>
          Billing History
        </Typography>
        <SearchHeader
          page={page}
          items={transactions && transactions.totalCount < defaultPageSize ? transactions.totalCount : defaultPageSize}
          total={transactions?.totalCount}
          parentClassName={{
            '& .MuiTextField-root': {
              width: '300px',
            },
          }}
          onPressEnter={handlePressEnter}
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
          filterOption={filterData}
          searchPlaceholder="Search Invoices"
          unitName={TABLES_ENTITIES.INVOICES}
        />

        <Box sx={{flex: '1', overflowY: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
          {isDataLoaded ? (
            transactions && transactions?.totalCount > 0 ? (
              <TableContainer>
                <Table sx={{minWidth: 650}} aria-label="enhanced table">
                  <colgroup>
                    <col style={{width: '50%'}} />
                    <col style={{width: '50%'}} />
                    <col style={{width: 'min-content'}} />
                    <col style={{width: 'min-content'}} />
                    <col style={{width: 'min-content'}} />
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice</TableCell>
                      <TableCell>Billing admin</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions?.data?.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell sx={billingHistory.tableCell}>
                          <Box sx={[billingHistory.fileWrap, billingHistory.tableCell]}>
                            <Box sx={billingHistory.iconWrap}>
                              <PdfIcon />
                            </Box>
                            <Box>
                              <Typography sx={billingHistory.invoiceName}>invoice.pdf</Typography>
                              <p style={billingHistory.neutral300}>{dayjs(row.date).format(MMMM_D_YYYY)}</p>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={billingHistory.tableCell}>
                            <p style={billingHistory.weight700}>{row.billingAdminName}</p>
                            <p style={billingHistory.neutral300}>{row.billingAdminEmail}</p>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={billingHistory.tableCell}>
                            <p style={billingHistory.amount}>{row.amount}</p>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={billingHistory.tableCell}>
                            <Box sx={billingHistory.statusWrap(StatusColor[row.status])}>
                              <p className="status">{row.status}</p>
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <EmptyTableData text="No data found" />
            )
          ) : (
            <Loader parentClassName={billingHistory.loader} size={50} />
          )}
        </Box>

        {isDataLoaded && transactions && transactions?.totalCount > 0 && (
          <Box sx={billingHistory.pagination}>
            <Pagination
              page={page}
              pageSize={defaultPageSize || 0}
              total={transactions?.totalCount}
              onChange={(step) => setPage(step)}
            />
          </Box>
        )}
      </Box>
      <Modal
        open={isOpenFeeModal}
        onClose={handleFeeModal}
        isShowCloseButton
        isShowSaveButton
        onSave={updatePlatformFee}
        isSaveButtonDisable={+fee <= 0}
        title="Update Platform fee"
        description="The percentage of all transactions that will be collected by the platform"
        sx={profileFormStyle.customeModal}
      >
        <Input type="number" value={fee} onChange={setFee} sx={{width: '100%', marginTop: '32px'}} />
      </Modal>
    </Box>
  );
};

export default AdminBillingHistory;
